import React, { useState, useEffect } from 'react';
import api from 'state/axios';
import { useTheme, Box, Typography  } from '@mui/material';
import { ResponsiveHeatMap } from '@nivo/heatmap';


const HeatMap = ({ data = [], title = "" }) => {
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const neutralDark = theme.palette.neutral.dark;
  const neutralLight = theme.palette.neutral.light;
  const textPrimaryColor = theme.palette.text.primary;
 
  const maxValue = data.reduce((max, item) => {
    const itemMax = Math.max(...item.data.map(d => d.y));
    return Math.max(max, itemMax);
  }, 0);
    
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [delayCodes, setDelayCodes] = useState({});

  useEffect(() => {
    const fetchDelayCodes = async () => {
      try {
        setLoading(true);

        const response = await api.get('/delaycodes');

        setDelayCodes(response.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDelayCodes();
  }, []);
  
  if (loading || !data || data.length === 0 || !Array.isArray(data)) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>        
        <Typography>Nothing to display</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>        
        <Typography>error.message</Typography>
      </Box>
    );
  }

  const CustomTooltip = ({ cell }) => {
    const etdRange = cell.id ? cell.id.split('.')[0].trim() : 'Unknown';    
    const delayIndex = cell.data.x || 'Unknown';
    const description = delayCodes[delayIndex]?.description || 'No description';    
    const count = cell.value || 0;

    return (
      <Box
        sx={{
          color: textPrimaryColor,
          background: neutralLight,
          padding: 1,
          borderRadius: 1,
        }}
      >
        <Typography variant="body2">
          Etd Range: {etdRange} o'clock
        </Typography>
        <Typography variant="body2">
          Delay Code: {delayIndex} - {description}
        </Typography>
        <Typography variant="body2">
          Count: {count}
        </Typography>
      </Box>
    );
  };
  
  
  return (
    <Box 
      position="relative" 
      sx={{ height: '100%', width: '100%' }}
    >
      {title && (
        <Typography 
          variant="h6" 
          align="center"
        >
          {title}
        </Typography>
      )}

      <ResponsiveHeatMap
        data={data}
        margin={{ 
          top: title ? 30 : 40, 
          right: 30, 
          bottom: title ? 120 : 100,
          left: 80 
        }}
        valueFormat=">-.2~s"
        axisRight={{ tickValues: [] }}        
        axisBottom={{
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          legend: 'Delay Code',
          legendPosition: 'middle',
          legendOffset: 45,
          truncateTickAt: 0
        }}    
        axisLeft={{
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          legend: 'Etd Range',
          legendPosition: 'middle',
          legendOffset: -65,
          truncateTickAt: 0
        }} 
        colors={{
          type: 'diverging',
          scheme: 'turbo',
          divergeAt: 0.5,
          minValue: 0,
          maxValue: maxValue,
        }}
        emptyColor="#555555"
        borderRadius={4}
        legends={[
          {
            anchor: 'bottom',
            translateX: 0,
            translateY: 70,
            length: 400,
            thickness: 8,
            direction: 'row',
            tickPosition: 'after',
            tickSize: 3,
            tickSpacing: 4,
            tickOverlap: false,
            tickFormat: '>-.2~s',
            title: 'Value →',
            titleAlign: 'start',
            titleOffset: 4,            
          }
        ]}
        // hoverTarget="cell"
        theme={{
          axis: {
            domain: {
              line: {
                stroke: neutralDark,
              },
            },
            legend: {
              text: {
                fill: textPrimaryColor,
              },
            },
            ticks: {
              line: {
                stroke: primaryDark,
                strokeWidth: 1,
              },
              text: {
                fill: textPrimaryColor,
              },
            },
          },
          tooltip: {
            container: {
              background: neutralLight,
              color: textPrimaryColor,
              borderRadius: '4px',
              padding: '8px',
            },
          },                 
        }}
        
        tooltip={CustomTooltip}
      />
    </Box>
  );
};

export default HeatMap;
